/**
 * The writes on Recruiting and Learning (WALLIYO-0131).
 *
 * Moving somebody says plainly what "hired" does and does not do: it records
 * that they were hired. Inviting them is Joining's, where it already lives and
 * is already audited.
 */

import type { CandidateStage, Course, JobCandidate, JobOpening } from "@wallyo/contracts/console/hr";
import type { TeamListItem } from "@wallyo/contracts/console/configuration";
import { useState } from "react";
import { addCandidate, addCourse, editJob, moveCandidate, openJob } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t, type MessageId } from "../../i18n";
import { ReasonDialogView, useIntentWrite } from "../team/TeamDialogs";

type Common = { onClose: () => void; onReload: () => void; onDone: (message: string) => void };

const STAGES: readonly { id: CandidateStage; label: MessageId }[] = [
  { id: "applied", label: "recruiting.stage.applied" },
  { id: "screening", label: "recruiting.stage.screening" },
  { id: "interview", label: "recruiting.stage.interview" },
  { id: "offer", label: "recruiting.stage.offer" },
  { id: "hired", label: "recruiting.stage.hired" },
  { id: "declined", label: "recruiting.stage.declined" },
  { id: "withdrawn", label: "recruiting.stage.withdrawn" },
];

const STATUSES: readonly { id: JobOpening["status"]; label: MessageId }[] = [
  { id: "open", label: "recruiting.status.open" },
  { id: "on_hold", label: "recruiting.status.onHold" },
  { id: "filled", label: "recruiting.status.filled" },
  { id: "closed", label: "recruiting.status.closed" },
];

export function JobDialog(props: Common & { opening: JobOpening | null; teams: readonly TeamListItem[] }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const { opening } = props;
  const [title, setTitle] = useState(opening?.title ?? "");
  const [staffTeamId, setStaffTeamId] = useState(opening?.staffTeamId ?? "");
  const [description, setDescription] = useState(opening?.description ?? "");
  const [status, setStatus] = useState<JobOpening["status"]>(opening?.status ?? "open");
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("recruiting.jobForm.title")}
      body={<p className="meta">{t("recruiting.jobForm.description")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("recruiting.jobForm.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          title.trim().length < 2 ? t("recruiting.jobForm.name") : null,
          (key) =>
            opening
              ? editJob(
                  client,
                  opening.jobOpeningId,
                  {
                    title: title.trim(),
                    staffTeamId: staffTeamId || null,
                    description: description.trim() || null,
                    status,
                    reason: reason.trim(),
                    version: opening.version,
                  },
                  { idempotencyKey: key },
                )
              : openJob(
                  client,
                  {
                    title: title.trim(),
                    staffTeamId: staffTeamId || null,
                    description: description.trim() || null,
                    reason: reason.trim(),
                  },
                  { idempotencyKey: key },
                ),
          ({ opening: saved }: { opening: JobOpening }) =>
            props.onDone(
              opening
                ? t("recruiting.jobEdited", { title: saved.title })
                : t("recruiting.jobOpened", { title: saved.title }),
            ),
        )
      }
    >
      <div className="field">
        <label htmlFor="job-title">{t("recruiting.jobForm.name")}</label>
        <input
          id="job-title"
          type="text"
          value={title}
          onChange={(event) => {
            write.edited();
            setTitle(event.currentTarget.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="job-team">{t("recruiting.jobForm.team")}</label>
        <select
          id="job-team"
          value={staffTeamId}
          onChange={(event) => {
            write.edited();
            setStaffTeamId(event.currentTarget.value);
          }}
        >
          <option value="">{t("recruiting.jobForm.noTeam")}</option>
          {props.teams.map((team) => (
            <option key={team.staffTeamId} value={team.staffTeamId}>
              {team.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="job-description">{t("recruiting.jobForm.description")}</label>
        <textarea
          id="job-description"
          value={description}
          onChange={(event) => {
            write.edited();
            setDescription(event.currentTarget.value);
          }}
        />
      </div>
      {opening ? (
        <div className="field">
          <label htmlFor="job-status">{t("recruiting.jobForm.status")}</label>
          <select
            id="job-status"
            value={status}
            onChange={(event) => {
              write.edited();
              setStatus(event.currentTarget.value as JobOpening["status"]);
            }}
          >
            {STATUSES.map((each) => (
              <option key={each.id} value={each.id}>
                {t(each.label)}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </ReasonDialogView>
  );
}

export function CandidateDialog(props: Common & { opening: JobOpening }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("recruiting.candidateForm.title")}
      body={<p className="meta">{props.opening.title}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("recruiting.candidateForm.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          name.trim().length < 2 ? t("recruiting.candidateForm.name") : null,
          (key) =>
            addCandidate(
              client,
              props.opening.jobOpeningId,
              {
                name: name.trim(),
                phone: phone.trim() || null,
                email: email.trim() || null,
                reason: reason.trim(),
              },
              { idempotencyKey: key },
            ),
          ({ candidate }) => props.onDone(t("recruiting.candidateAdded", { name: candidate.name })),
        )
      }
    >
      <div className="field">
        <label htmlFor="cand-name">{t("recruiting.candidateForm.name")}</label>
        <input
          id="cand-name"
          type="text"
          value={name}
          onChange={(event) => {
            write.edited();
            setName(event.currentTarget.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="cand-phone">{t("recruiting.candidateForm.phone")}</label>
        <input
          id="cand-phone"
          type="tel"
          value={phone}
          onChange={(event) => {
            write.edited();
            setPhone(event.currentTarget.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="cand-email">{t("recruiting.candidateForm.email")}</label>
        <input
          id="cand-email"
          type="email"
          value={email}
          onChange={(event) => {
            write.edited();
            setEmail(event.currentTarget.value);
          }}
        />
      </div>
    </ReasonDialogView>
  );
}

export function MoveCandidateDialog(props: Common & { candidate: JobCandidate }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const { candidate } = props;
  const [stage, setStage] = useState<CandidateStage>(candidate.stage);
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("recruiting.moveForm.title")}
      body={
        <p className="meta">
          {candidate.name}
          <br />
          {t("recruiting.moveForm.hired")}
        </p>
      }
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("recruiting.moveForm.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          null,
          (key) =>
            moveCandidate(
              client,
              candidate.jobCandidateId,
              { stage, reason: reason.trim(), version: candidate.version },
              { idempotencyKey: key },
            ),
          ({ candidate: moved }) =>
            props.onDone(
              t("recruiting.candidateMoved", {
                name: moved.name,
                stage: t(STAGES.find((each) => each.id === moved.stage)!.label),
              }),
            ),
        )
      }
    >
      <div className="field">
        <label htmlFor="move-stage">{t("recruiting.moveForm.stage")}</label>
        <select
          id="move-stage"
          value={stage}
          onChange={(event) => {
            write.edited();
            setStage(event.currentTarget.value as CandidateStage);
          }}
        >
          {STAGES.map((each) => (
            <option key={each.id} value={each.id}>
              {t(each.label)}
            </option>
          ))}
        </select>
      </div>
    </ReasonDialogView>
  );
}

export function CourseDialog(props: Common) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [title, setTitle] = useState("");
  const [audience, setAudience] = useState("");
  const [minutes, setMinutes] = useState("");
  const [requiredFor, setRequiredFor] = useState("");
  const [everybody, setEverybody] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("learning.courseForm.title")}
      body={<p className="meta">{t("learning.courseForm.requiredHelp")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("learning.courseForm.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          title.trim().length < 2 ? t("learning.courseForm.name") : null,
          (key) =>
            addCourse(
              client,
              {
                title: title.trim(),
                audience: audience.trim() || null,
                minutes: minutes === "" ? null : Number(minutes),
                requiredForSettingId: requiredFor.trim() || null,
                requiredOfEverybody: everybody,
                reason: reason.trim(),
              },
              { idempotencyKey: key },
            ),
          ({ course }: { course: Course }) => props.onDone(t("learning.courseAdded", { title: course.title })),
        )
      }
    >
      <div className="field">
        <label htmlFor="course-title">{t("learning.courseForm.name")}</label>
        <input
          id="course-title"
          type="text"
          value={title}
          onChange={(event) => {
            write.edited();
            setTitle(event.currentTarget.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="course-audience">{t("learning.courseForm.audience")}</label>
        <input
          id="course-audience"
          type="text"
          value={audience}
          onChange={(event) => {
            write.edited();
            setAudience(event.currentTarget.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="course-minutes">{t("learning.courseForm.minutes")}</label>
        <input
          id="course-minutes"
          type="number"
          min={1}
          value={minutes}
          onChange={(event) => {
            write.edited();
            setMinutes(event.currentTarget.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="course-required">{t("learning.courseForm.required")}</label>
        <input
          id="course-required"
          type="text"
          value={requiredFor}
          onChange={(event) => {
            write.edited();
            setRequiredFor(event.currentTarget.value);
          }}
        />
      </div>
      <label className="field row">
        <input
          type="checkbox"
          checked={everybody}
          onChange={(event) => {
            write.edited();
            setEverybody(event.currentTarget.checked);
          }}
        />
        <span>{t("learning.courseForm.everybody")}</span>
      </label>
    </ReasonDialogView>
  );
}
